import React, { Component, PropTypes } from 'react'
import CSSModules from 'react-css-modules'
import config from '../../config'
import format from '../../helpers/format'
import {
  Link,
  Grade
} from '../../components'

import Styles from './TargetDateSeriesGradeDescription.css'

/**
 *
 * Displays the description of the grade earned by a target date series for
 * one of the issues.
 *
 * Depending on which of the issues is selected, it will show different:
 *
 * - background image
 * - icon
 * - grade label
 * - methodology link
 * - "the issues" link
 *
 * Depending on which of the target date series is selected; as well as which
 * of the issues is selected; it will show different:
 *
 * - headline
 * - grade
 * - sentence describing the grade
 *
 */
@CSSModules(Styles, {allowMultiple:true})
export default class TargetDateSeriesGradeDescription extends Component {
  static propTypes = {
    // three letters, fff/dff/gef/gff/pff/wff/tff
    iyvCode: PropTypes.string.isRequired,
    // the target date series object
    targetDateSeries: PropTypes.object.isRequired,
  }
  constructor(props) {
    super(props)
    this.state = this.getGradeDescriptionState(
      props.iyvCode,
      props.targetDateSeries
    )
  }

  componentWillReceiveProps(props){
    this.setState(
      this.getGradeDescriptionState(
        props.iyvCode,
        props.targetDateSeries
      )
    )
  }

  getGradeDescriptionState(iyvCode, targetDateSeries){
    let gradeHeadlineDescription = {}
    switch (iyvCode) {
      case 'fff':
        gradeHeadlineDescription = this.getFffGradeDescr(targetDateSeries)
        break
      case 'ffb':
        gradeHeadlineDescription = this.getFfbGradeDescr(targetDateSeries)
        break
      case 'ffi':
        gradeHeadlineDescription = this.getFfiGradeDescr(targetDateSeries)
        break
      case 'dff':
        gradeHeadlineDescription = this.getDffGradeDescr(targetDateSeries)
        break
      case 'gef':
        gradeHeadlineDescription = this.getGefGradeDescr(targetDateSeries)
        break
      case 'gff':
        gradeHeadlineDescription = this.getGffGradeDescr(targetDateSeries)
        break
      case 'pff':
        gradeHeadlineDescription = this.getPffGradeDescr(targetDateSeries)
        break
      case 'wff':
        gradeHeadlineDescription = this.getWffGradeDescr(targetDateSeries)
        break
      case 'tff':
        gradeHeadlineDescription = this.getTffGradeDescr(targetDateSeries)
        break
    }

    // get the grade as int and letter
    const gradeField = {
      fff: 'grade_fossil',
      ffb: 'grade_fossil_bank',
      ffi: 'grade_fossil_insure',
      dff: 'grade_deforestation',
      gef: 'grade_gender_equality',
      gff: 'grade_guns',
      pff: 'grade_prison_industrial_complex',
      wff: 'grade_military',
      tff: 'grade_tobacco',
    }[iyvCode || null]

    const gradeAsInt = targetDateSeries[gradeField]

    // get the label for the grade
    const issueLabel = {
      fff: 'Fossil fuel',
      ffb: 'Fossil fuel finance',
      ffi: 'Fossil fuel insurance',
      dff: 'Deforestation',
      gef: 'Gender equality',
      gff: 'Civilian firearms',
      pff: 'Prison industrial complex',
      wff: 'Military weapons',
      tff: 'Tobacco'
    }[iyvCode] || null

    // get the text for the methodology link
    const methodologyLinkText = {
      fff: 'How we grade funds on fossil fuel investments',
      ffb: 'How we grade funds on investments in fossil fuel finance',
      ffi: 'How we grade funds on investments in fossil fuel insurance',
      dff: 'How we grade funds on deforestation-risk investments',
      gef: 'How we grade funds on gender equality',
      gff: 'How we grade funds on civilian firearms investments',
      pff: 'How we grade funds on prison industrial complex investments',
      wff: 'How we grade funds on military weapons investments',
      tff: 'How we grade funds on tobacco investments'
    }[iyvCode] || null

    const methodologyNavId = {
      fff: 'fossil-fuel-grade',
      ffb: 'fossil-finance-grade',
      ffi: 'fossil-insurance-grade',
      dff: 'deforestation-grade',
      gef: 'gender-equality-grade',
      gff: 'civilian-firearms-grade',
      pff: 'prison-industrial-complex-grade',
      wff: 'military-weapons-grade',
      tff: 'tobacco-grade',
    }[iyvCode] || null

    return {
      iyvCode,
      headline: gradeHeadlineDescription.headline,
      descriptionSentence: gradeHeadlineDescription.descriptionSentence,
      gradeAsInt,
      issueLabel,
      methodologyLinkText,
      methodologyNavId
    }
  }

  /**
   * SITE-SPECIFIC FUNCTIONS
   */
  getFffGradeDescr(targetDateSeries){
    // targetDateSeries[cnfg.grade]
    const val = format.perc(targetDateSeries.fossil_fuel_weight)
    const headline = (
      <span>
        The {targetDateSeries.strategy_name} is <span styleName={'score-color-'+targetDateSeries.grade_fossil}>{val}</span> invested in fossil fuels.
      </span>
    )
    const descriptionSentence = {
      1: (<span><b>0%</b> fossil fuel exposure is assigned a grade of A.</span>),
      2: (<span>Fossil fuel exposure of <b>{val}</b>  places the fund in the <b>B&nbsp;tier</b>, between 0% and 3% exposure.</span>),
      3: (<span>Fossil fuel exposure of <b>{val}</b>  places the fund in the <b>C&nbsp;tier</b>, between 3% and 5.5% exposure.</span>),
      4: (<span>Fossil fuel exposure of <b>{val}</b>  places the fund in the <b>D&nbsp;tier</b>, between 5.5% and 9% exposure.</span>),
      5: (<span>Fossil fuel exposure of <b>{val}</b>  places the fund in the <b>F&nbsp;tier</b>, between 9% and 100% exposure.</span>)
    }[targetDateSeries.grade_fossil]
    if (!descriptionSentence) { return null }
    return {
      headline,
      descriptionSentence,
    }
  }

  getFfbGradeDescr(targetDateSeries){
    const _score = format.number(targetDateSeries.fossil_bank_score, false, false, 1)
    const headline = (
      <span>
        The {targetDateSeries.strategy_name} has a score of <span styleName={'score-color-'+targetDateSeries.grade_fossil_bank}>{_score}</span> for fossil fuel finance.
      </span>
    )
    let descriptionSentence = null
    switch (targetDateSeries.grade_fossil_bank) {
      case 1:
        descriptionSentence = 'No holdings flagged by our fossil fuel finance screen.'
        break
      case 2:
        descriptionSentence = (<span>Fossil fuel finance score of <b>{_score}</b> places the fund in the <b>B&nbsp;tier</b>.</span>)
        break
      case 3:
        descriptionSentence = (<span>Fossil fuel finance score of <b>{_score}</b> places the fund in the <b>C&nbsp;tier</b>.</span>)
        break
      case 4:
        descriptionSentence = (<span>Fossil fuel finance score of <b>{_score}</b> places the fund in the <b>D&nbsp;tier</b>.</span>)
        break
      case 5:
        descriptionSentence = (<span>Fossil fuel finance score of <b>{_score}</b> places the fund in the <b>F&nbsp;tier</b>.</span>)
        break
    }
    if (!descriptionSentence) { return null }
    return {
      headline,
      descriptionSentence,
    }
  }

  getFfiGradeDescr(targetDateSeries){
    const _score = format.number(targetDateSeries.fossil_insure_score, false, false, 1)
    const headline = (
      <span>
        The {targetDateSeries.strategy_name} has a score of <span styleName={'score-color-'+targetDateSeries.grade_fossil_insure}>{_score}</span> for fossil fuel insurance.
      </span>
    )
    let descriptionSentence = null
    switch (targetDateSeries.grade_fossil_insure) {
      case 1:
        descriptionSentence = (<span>Fossil fuel insurance score of <b>{_score}</b> places the fund in the <b>A&nbsp;tier</b>.</span>)
        break
      case 2:
        descriptionSentence = (<span>Fossil fuel insurance score of <b>{_score}</b> places the fund in the <b>B&nbsp;tier</b>.</span>)
        break
      case 3:
        descriptionSentence = (<span>Fossil fuel insurance score of <b>{_score}</b> places the fund in the <b>C&nbsp;tier</b>.</span>)
        break
      case 4:
        descriptionSentence = (<span>Fossil fuel insurance score of <b>{_score}</b> places the fund in the <b>D&nbsp;tier</b>.</span>)
        break
      case 5:
        descriptionSentence = (<span>Fossil fuel insuranceg score of <b>{_score}</b> places the fund in the <b>F&nbsp;tier</b>.</span>)
        break
    }
    if (!descriptionSentence) { return null }
    return {
      headline,
      descriptionSentence,
    }
  }

  getDffGradeDescr(targetDateSeries){
    const val_p = format.perc(targetDateSeries.df_p_weight)
    const val_all = format.perc(targetDateSeries.df_all_weight)
    const headline = (
      <span>
        The {targetDateSeries.strategy_name} is <span styleName={'score-color-'+targetDateSeries.grade_deforestation}>{val_p}</span> invested in deforestation-risk agribusiness.
      </span>
    )
    const descriptionSentence = {
      1: (<span><b>0%</b> deforestation-risk screen exposure is assigned a grade of A.</span>),
      2: (<span><b>0%</b> deforestation-risk agribusiness exposure. Deforestation-risk finance/consumer brands exposure of <b>{val_all}</b> places the fund in the <b>B&nbsp;tier</b>.</span>),
      3: (<span>Deforestation-risk agribusiness exposure of <b>{val_p}</b> places the fund in the <b>C&nbsp;tier</b>, between 0% and 0.5% exposure.</span>),
      4: (<span>Deforestation-risk agribusiness exposure of <b>{val_p}</b> places the fund in the <b>D&nbsp;tier</b>, between 0.5% and 1.5% exposure.</span>),
      5: (<span>Deforestation-risk agribusiness exposure of <b>{val_p}</b> places the fund in the <b>F&nbsp;tier</b>, between 1.5% and 100% exposure.</span>)
    }[targetDateSeries.grade_deforestation]
    if (!descriptionSentence) { return null }
    return {
      headline,
      descriptionSentence,
    }
  }

  getGefGradeDescr(targetDateSeries){
    const val_score = Math.round(targetDateSeries.gef_score)
    const headline = (
      <span>
        The {targetDateSeries.strategy_name} has a gender equality score of <span styleName={'score-color-'+targetDateSeries.grade_gender_equality}>{val_score}</span> out of 100 points.
      </span>
    )
    const descriptionSentence = {
      1: (<span>Gender equality score of <b>{val_score} out of 100 points</b> places the fund in the <b>A&nbsp;tier</b>, the top 20% of the Allocation Funds group.</span>),
      2: (<span>Gender equality score of <b>{val_score} out of 100 points</b> places the fund in the <b>B&nbsp;tier</b>, the second 20% of the Allocation Funds group.</span>),
      3: (<span>Gender equality score of <b>{val_score} out of 100 points</b> places the fund in the <b>C&nbsp;tier</b>, the middle 20% of the Allocation Funds group.</span>),
      4: (<span>Gender equality score of <b>{val_score} out of 100 points</b> places the fund in the <b>D&nbsp;tier</b>, the second-last 20% of the Allocation Funds group.</span>),
      5: (<span>Gender equality score of <b>{val_score} out of 100 points</b> places the fund in the <b>F&nbsp;tier</b>, the bottom 20% of the Allocation Funds group.</span>)
    }[targetDateSeries.grade_gender_equality]
    if (!descriptionSentence) { return null }
    return {
      headline,
      descriptionSentence,
    }
  }

  getGffGradeDescr(targetDateSeries){
    const val_high = format.perc(targetDateSeries.wff_gun_manufacturers_weight)
    const val_all = format.perc(targetDateSeries.wff_civ_weight)
    const headline = (
      <span>
        The {targetDateSeries.strategy_name} has <span styleName={'score-color-'+targetDateSeries.grade_guns}>{val_high}</span> exposure to civilian firearm manufacturers.
      </span>
    )
    const descriptionSentence = {
      1: (<span><b>0%</b> civilian firearms screen exposure places the fund in the <b>A&nbsp;tier</b>.</span>),
      2: (<span><b>0%</b> gun manufacturers exposure. Gun retailers exposure of <b>{val_all}</b> places the fund in the <b>B&nbsp;tier</b>.</span>),
      3: (<span>Gun manufacturers exposure of <b>{val_high}</b> places the fund in the <b>C&nbsp;tier</b>, between 0% and 0.1% exposure.</span>),
      4: (<span>Gun manufacturers exposure of <b>{val_high}</b> places the fund in the <b>D&nbsp;tier</b>, between 0.1% and 0.4% exposure.</span>),
      5: (<span>Gun manufacturers exposure of <b>{val_high}</b> places the fund in the <b>F&nbsp;tier</b>, between 0.4% and 100% exposure.</span>)
    }[targetDateSeries.grade_guns]
    if (!descriptionSentence) { return null }
    return {
      headline,
      descriptionSentence,
    }
  }

  getPffGradeDescr(targetDateSeries){
    const val_extreme = format.perc(targetDateSeries.pff_extreme_weight)
    const val_high = format.perc(targetDateSeries.pff_high_weight)
    const val_all = format.perc(targetDateSeries.pff_all_weight)
    const headline = (
      <span>
        The {targetDateSeries.strategy_name} has <span styleName={'score-color-'+targetDateSeries.grade_prison_industrial_complex}>{val_high}</span> exposure to the high-risk prison industrial complex screen.
      </span>
    )
    let descriptionSentence = {
      1: (<span><b>0%</b> prison industrial complex screen exposure places the fund in the <b>A&nbsp;tier</b>.</span>),
      2: (<span><b>0%</b> exposure to higher-risk prison industrial complex screen. Other prison industry/border industry exposure of <b>{val_all}</b> places the fund in the <b>B&nbsp;tier</b>.</span>),
      3: (<span>Exposure to higher-risk prison industrial complex screen of <b>{val_high}</b> places the fund in the <b>C&nbsp;tier</b>, between 0% and 1% exposure.</span>),
      4: (<span>Exposure to higher-risk prison industrial complex screen of <b>{val_high}</b> places the fund in the <b>D&nbsp;tier</b>, between 1% and 2% exposure.</span>),
      5: (<span>Exposure to higher-risk prison industrial complex screen of <b>{val_high}</b> places the fund in the <b>F&nbsp;tier</b>, between 2% and 100% exposure.</span>)
    }[targetDateSeries.grade_prison_industrial_complex]
    if (targetDateSeries.pff_extreme_weight > 0) {
      descriptionSentence = (<span>Exposure to private prison operators of <b>{val_extreme}</b>. Any exposure to private prison operators places the fund in the <b>F&nbsp;tier</b>.</span>)
    }
    if (!descriptionSentence) { return null }
    return {
      headline,
      descriptionSentence,
    }
  }

  getWffGradeDescr(targetDateSeries){
    // const val_controversial = format.perc(targetDateSeries.wff_cluster_munitions_weight)
    // const val_nuclear = format.perc(targetDateSeries.wff_nuclear_weapons_weight)
    const val_all = format.perc(targetDateSeries.wff_mil_weight)
    const headline = (
      <span>
        The {targetDateSeries.strategy_name} has <span styleName={'score-color-'+targetDateSeries.grade_military}>{val_all}</span> exposure to arms manufacturers and military contractors.
      </span>
    )
    const descriptionSentence = {
      1: (<span>
            No holdings flagged for our military weapon screens. Assigned a
            grade of A.
          </span>),
      2: (<span>
            Fund has no investments in
            nuclear weapons or controversial weapons,
            but is invested in military contractors,
            below the threshold of 2.5%. Assigned a grade of B.
          </span>),
      3: (<span>
            Fund has investments in nuclear weapons and/or
            controversial weapons, and overall
            military contractors exposure is
            below the threshold of 2.5%. Assigned a grade of C.
          </span>),
      4: (<span>
            Fund is invested in military contractors
            above the threshold of 2.5% and below the threshold of 4%.
            Assigned a grade of D.
          </span>),
      5: (<span>
            Fund is invested in military contractors
            above the threshold of 4%.
            Assigned a grade of F.
          </span>)
    }[targetDateSeries.grade_military]
    if (!descriptionSentence) { return null }
    // let descriptionSentence = {
    //   1: (<span><b>0%</b> military weapons screen exposure places the fund in the <b>A&nbsp;tier</b>.</span>),
    //   2: (<span>Military weapons screen exposure of <b>{val_all}</b> places the fund in the <b>B&nbsp;tier</b>, between 0% and 1% exposure.</span>),
    //   3: (<span>Military weapons screen exposure of <b>{val_all}</b> places the fund in the <b>C&nbsp;tier</b>, between 1% and 2% exposure.</span>),
    //   4: (<span>Military weapons screen exposure of <b>{val_all}</b> places the fund in the <b>D&nbsp;tier</b>, between 2% and 3% exposure.</span>),
    //   5: (<span>Military weapons screen exposure of <b>{val_all}</b> places the fund in the <b>F&nbsp;tier</b>, between 3% and 100% exposure.</span>)
    // }[targetDateSeries.grade_military]
    // if (!descriptionSentence) { return null }
    // if (targetDateSeries.wff_cluster_munitions_weight > 0) {
    //   descriptionSentence = (<span>Exposure to cluster munitions screen of <b>{val_extreme}</b>. Any exposure to cluster munitions screen places the fund in the <b>F&nbsp;tier</b>.</span>)
    // }
    return {
      headline,
      descriptionSentence,
    }
  }

  getTffGradeDescr(targetDateSeries){
    const val_high = format.perc(targetDateSeries.t_weight)
    const val_all = format.perc(targetDateSeries.t_all_weight)
    const headline = (
      <span>
        The {targetDateSeries.strategy_name} has <span styleName={'score-color-'+targetDateSeries.grade_tobacco}>{val_high}</span> exposure to tobacco producers.
      </span>
    )
    const descriptionSentence = {
      1: (<span><b>0%</b> tobacco screen exposure places the fund in the <b>A&nbsp;tier</b>.</span>),
      2: (<span><b>0%</b> tobacco producers exposure. Tobacco-promoting entertainment studios exposure of <b>{val_all}</b> places the fund in the <b>B&nbsp;tier</b>.</span>),
      3: (<span>Tobacco producers exposure of <b>{val_high}</b> places the fund in the <b>C&nbsp;tier</b>, between 0% and 0.5% exposure.</span>),
      4: (<span>Tobacco producers exposure of <b>{val_high}</b> places the fund in the <b>D&nbsp;tier</b>, between 0.5% and 1.2% exposure.</span>),
      5: (<span>Tobacco producers exposure of <b>{val_high}</b> places the fund in the <b>F&nbsp;tier</b>, between 1.2% and 100% exposure.</span>)
    }[targetDateSeries.grade_tobacco]
    if (!descriptionSentence) { return null }
    return {
      headline,
      descriptionSentence,
    }
  }

  //
  render() {
    const { iyvCode, headline, descriptionSentence, gradeAsInt,
            issueLabel, methodologyLinkText,
            methodologyNavId } = this.state

    // get the icon
    const iconWhite = {
      fff: require( '../../../static/images/icons/fff-icon-white.svg' ),
      ffb: require( '../../../static/images/icons/ffb-icon-white.svg' ),
      ffi: require( '../../../static/images/icons/ffi-icon-white.svg' ),
      dff: require( '../../../static/images/icons/dff-icon-white.svg' ),
      gef: require( '../../../static/images/icons/gef-icon-white.svg' ),
      gff: require( '../../../static/images/icons/gff-icon-white.svg' ),
      pff: require( '../../../static/images/icons/pff-icon-white.svg' ),
      wff: require( '../../../static/images/icons/wff-icon-white.svg' ),
      tff: require( '../../../static/images/icons/tff-icon-white.svg' )
    }[iyvCode] || null

    //
    //
    return (
      <div styleName='grade-descrp-flex'>
        { /* left (top on mobile */ }
        <div styleName='grade-descrp-flex-cell'>
          <div styleName={'site-img img-' + iyvCode}>
            <div styleName='site-img-gray'>
              <h2 styleName='site-img-headline'>
                {
                  iconWhite ?
                    (
                      <div styleName='site-img-icon-bbl'>
                        <img
                          styleName='site-img-icon'
                          src={iconWhite}
                          alt={issueLabel + ' grade'}
                        />
                      </div>
                    ) :
                    null
                }
                {headline}
              </h2>
            </div>
          </div>
        </div>
        { /* right (bottom on mobile */ }
        <div styleName='grade-descrp-flex-cell'>
          <div styleName='scorecard-header-grade-flex'>
            <div styleName='icon-label-flex'>
              <h2 styleName='grade-descrp-grade-label'>
                {issueLabel} grade
              </h2>
            </div>
            <div styleName='scorecard-header-grade'>
              <Grade grade={gradeAsInt} block/>
            </div>
          </div>
          <div styleName='grade-descrp'>
            {descriptionSentence}
          </div>
          <div styleName='methodology-link'>
            <span>Methodology</span>
            <Link
              to={config.routes.methodology + '#' + methodologyNavId}
              title='How it works'
            >
              {methodologyLinkText}
            </Link>
          </div>
        </div>
      </div>
    )
  }
}